// Recap — scrapbook is the default share artifact (ADR-0017 amended 2026-05-17).
//
// Renders client-side via generateScrapbookCollage(). User can preview all
// three variants (scrapbook / wrapped / flipbook) via the variant pills,
// download the PNG, share via Web Share API with file payload, or copy the
// link. Leaderboard chip surfaces when friends were also out that night.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { type ActiveStumbleDto, type NightDetailsDto, nightApi, stumblingApi } from '../../../api'
import { Icon } from '../../../components/Icon'
import { BackButton, TopBar } from '../../../components/TopBar'
import { track } from '../../../lib/analytics'
import {
  type RecapMetadata,
  type RecapVariant,
  generateRecapCollage,
} from '../../../lib/media/recapCollage'
import type { ScrapbookLeaderboardEntry } from '../../../lib/media/recapLayouts/scrapbook'

export const Route = createFileRoute('/night/$id/recap')({
  component: RecapRoute,
})

type RenderState =
  | { kind: 'loading' }
  | { kind: 'rendering' }
  | { kind: 'ready'; blob: Blob; url: string }
  | { kind: 'error'; message: string }

const VARIANTS: { id: RecapVariant; label: string }[] = [
  { id: 'scrapbook', label: 'scrapbook' },
  { id: 'wrapped', label: 'wrapped' },
  { id: 'flipbook', label: 'flipbook' },
]

function RecapRoute() {
  const { id: nightId } = Route.useParams()
  const navigate = useNavigate()
  const [details, setDetails] = useState<NightDetailsDto | null>(null)
  const [stumbles, setStumbles] = useState<ActiveStumbleDto[]>([])
  const [variant, setVariant] = useState<RecapVariant>('scrapbook')
  const [render, setRender] = useState<RenderState>({ kind: 'loading' })
  const [shareMessage, setShareMessage] = useState<string | null>(null)
  const lastUrlRef = useRef<string | null>(null)

  // CEO-5: fire `recap_viewed` once per mount (one event per recap page hit,
  // not per re-render). nightId is stable per route param so deps are sound.
  useEffect(() => {
    track('recap_viewed', { nightId })
  }, [nightId])

  // ── Load night + (best-effort) live board for the leaderboard chip ───────
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [nightRes, liveStumbles] = await Promise.all([
          nightApi.get(nightId),
          stumblingApi.live().catch(() => [] as ActiveStumbleDto[]),
        ])
        if (cancelled) return
        setDetails(nightRes.night)
        setStumbles(liveStumbles)
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'load failed'
        setRender({ kind: 'error', message })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [nightId])

  // Build the leaderboard rows from (a) drinks-by-venue in the night and
  // (b) any concurrent friends from /api/stumbling. Stable client-side derivation
  // so the chip is consistent across renders.
  const leaderboard: ScrapbookLeaderboardEntry[] = useMemo(() => {
    if (!details) return []
    const friendRows: ScrapbookLeaderboardEntry[] = stumbles.map((s) => ({
      name: s.ownerDisplayName.toLowerCase(),
      rank: 0,
      drinkCount: s.drinkCount,
      venueName: s.currentVenue?.name ?? null,
      isSelf: false,
    }))
    const selfRow: ScrapbookLeaderboardEntry = {
      name: 'you',
      rank: 0,
      drinkCount: details.totals.drinks,
      venueName: details.currentVenue?.venue.name ?? details.sessionVenues[0]?.venue.name ?? null,
      isSelf: true,
    }
    const all = [...friendRows, selfRow]
      .sort((a, b) => b.drinkCount - a.drinkCount)
      .slice(0, 3)
      .map((row, i) => ({ ...row, rank: i + 1 }))
    // Suppress the chip entirely when no friends were out — solo nights stay clean.
    return friendRows.length === 0 ? [] : all
  }, [details, stumbles])

  // ── Render canvas every time the selected variant changes ────────────────
  useEffect(() => {
    if (!details) return
    let cancelled = false
    setRender({ kind: 'rendering' })

    void (async () => {
      try {
        // F3: explicit per-selfie array so the scrapbook draws one polaroid
        // per selfie instead of collapsing same-venue duplicates into one.
        // Most recent first, capped at 6 (the layout's slot count).
        const selfies = details.media
          .filter((m) => m.publicUrl || m.storageKey || m.dataUrl)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
          .slice(0, 6)
          .map((m) => ({
            url: pickMediaUrl(m),
            venueName: venueNameForMedia(details, m.venueId),
            capturedAt: m.createdAt,
          }))
          .filter((s): s is { url: string; venueName: string; capturedAt: string } => s.url !== '')

        const metadata: RecapMetadata = {
          nightId,
          nightDate: new Date(details.night.startedAt),
          startedAt: new Date(details.night.startedAt),
          endedAt: details.night.endedAt ? new Date(details.night.endedAt) : null,
          venues: details.sessionVenues.map((sv) => {
            // Find the most recent media asset taken at this venue, if any.
            const photoUrl = pickSelfieForVenue(details, sv.venueId)
            return {
              name: sv.venue.name,
              arrivedAt: new Date(sv.arrivedAt),
              leftAt: sv.leftAt ? new Date(sv.leftAt) : null,
              photoUrl,
            }
          }),
          drinkTotals: {
            beer: details.totals.byType?.beer ?? 0,
            wine: details.totals.byType?.wine ?? 0,
            cocktail: details.totals.byType?.cocktail ?? 0,
            shot: details.totals.byType?.shot ?? 0,
          },
          // Pre-squad MVP: every night is solo. Passing [] (not undefined)
          // lets smart-title fire the "alone but legendary." branch when
          // the night earns it.
          participants: [],
          leaderboard: leaderboard.length > 0 ? leaderboard : undefined,
          selfies,
        }

        const blob = await generateRecapCollage([], metadata, { variant })
        if (cancelled) return
        const url = URL.createObjectURL(blob)
        if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current)
        lastUrlRef.current = url
        setRender({ kind: 'ready', blob, url })
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Render failed'
        setRender({ kind: 'error', message })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [details, nightId, variant, leaderboard])

  // Cleanup the trailing blob URL on unmount.
  useEffect(() => {
    return () => {
      if (lastUrlRef.current) {
        URL.revokeObjectURL(lastUrlRef.current)
        lastUrlRef.current = null
      }
    }
  }, [])

  async function handleShare() {
    if (render.kind !== 'ready') return
    const filename = `stumble-${variant}.png`
    const file = new File([render.blob], filename, { type: 'image/png' })
    const venueNames =
      details?.sessionVenues
        .map((sv) => sv.venue.name)
        .filter(Boolean)
        .slice(0, 4) ?? []
    const totalDrinks = details?.totals.drinks ?? 0
    const totalStops = details?.sessionVenues.length ?? 0
    const text = [
      'stumble.party',
      totalStops > 0 ? `${totalStops} stop${totalStops === 1 ? '' : 's'}` : null,
      totalDrinks > 0 ? `${totalDrinks} drink${totalDrinks === 1 ? '' : 's'}` : null,
      venueNames.length > 0 ? venueNames.join(' → ') : null,
    ]
      .filter(Boolean)
      .join(' · ')

    if (
      typeof navigator !== 'undefined' &&
      'canShare' in navigator &&
      'share' in navigator &&
      navigator.canShare?.({ files: [file] })
    ) {
      try {
        await navigator.share({ files: [file], title: 'My Stumble', text })
        track('recap_shared', { nightId, variant, method: 'web-share' })
        setShareMessage('shared!')
        setTimeout(() => setShareMessage(null), 1500)
        return
      } catch {
        /* user cancelled — fall through to download */
      }
    }
    triggerDownload(render.url, filename)
    track('recap_shared', { nightId, variant, method: 'download-fallback' })
    setShareMessage('downloaded!')
    setTimeout(() => setShareMessage(null), 1500)
  }

  function handleDownload() {
    if (render.kind !== 'ready') return
    triggerDownload(render.url, `stumble-${variant}.png`)
    track('recap_shared', { nightId, variant, method: 'download' })
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href)
      track('recap_shared', { nightId, variant, method: 'copy-link' })
      setShareMessage('link copied!')
      setTimeout(() => setShareMessage(null), 1500)
    } catch {
      setShareMessage('clipboard unavailable')
      setTimeout(() => setShareMessage(null), 1500)
    }
  }

  if (render.kind === 'loading' || (!details && render.kind !== 'error')) {
    return (
      <div className="stumble-screen">
        <TopBar
          left={<BackButton onClick={() => navigate({ to: '/history' })} />}
          eyebrow="recap"
          title="loading"
        />
        <div
          className="screen-body body muted"
          style={{ alignContent: 'center', textAlign: 'center' }}
        >
          <div className="spinner" aria-hidden style={{ marginInline: 'auto' }} />
          <p style={{ marginTop: 18 }}>loading your night…</p>
        </div>
      </div>
    )
  }

  if (render.kind === 'error' || !details) {
    return (
      <div className="stumble-screen">
        <TopBar
          left={<BackButton onClick={() => navigate({ to: '/history' })} />}
          eyebrow="error"
        />
        <div className="screen-body body" style={{ textAlign: 'center' }}>
          {render.kind === 'error'
            ? `couldn't render your recap: ${render.message}`
            : "couldn't load this recap."}
        </div>
      </div>
    )
  }

  const startedAt = new Date(details.night.startedAt)
  const dateLabel = startedAt
    .toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    .toUpperCase()

  return (
    // F5: cream/tan canvas is the screen-root background (edge-to-edge),
    // not a confined inner card.
    <div className="stumble-screen" style={{ background: 'var(--paper-warm)' }}>
      <TopBar
        left={<BackButton onClick={() => navigate({ to: '/history' })} />}
        eyebrow={`recap · ${variant}`}
        title={dateLabel}
      />
      {/* G1.6 round 2: flex column fills viewport. The screen-body sits in
          the .stumble-screen grid row `1fr`, so it gets explicit min-height:0
          + flex:1 instead of a hard `height: 100dvh` (which overflowed the
          grid track and confused the image's % constraints below). */}
      <div
        className="screen-body"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          gap: 10,
          paddingTop: 6,
        }}
      >
        {/* Variant switcher pills */}
        <div
          role="tablist"
          aria-label="Recap variant"
          className="row"
          style={{ gap: 6, justifyContent: 'center' }}
        >
          {VARIANTS.map((v) => {
            const active = v.id === variant
            return (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`recap-variant-${v.id}`}
                onClick={() => setVariant(v.id)}
                style={{
                  padding: '6px 14px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 14,
                  borderRadius: 999,
                  border: '2px solid var(--ink)',
                  background: active ? 'var(--ink)' : 'var(--paper)',
                  color: active ? 'var(--paper)' : 'var(--ink)',
                  boxShadow: active ? 'none' : '0 3px 0 var(--shadow-ink, rgba(0,0,0,0.18))',
                  cursor: 'pointer',
                  minHeight: 32,
                }}
              >
                {v.label}
              </button>
            )
          })}
        </div>

        {/* Canvas preview — flex-fills the available column space.
            G1.6 round 2: the parent gets `flex:1; min-height:0` so it claims
            the remaining vertical column space. The <img> is sized to
            width/height 100% with object-fit:contain so it FILLS the parent
            box (preserving aspect ratio via object-fit). Using `flex:1` +
            `maxHeight:100%` on the <img> directly was the prior bug — flex-1
            sets `flex-basis:0` on the main axis (width) while leaving cross
            axis (height) to the image's intrinsic 1080×1920, which the parent
            then refused to stretch because alignItems defaulted to stretch
            but the parent itself had no resolved height (cascade issue). */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'stretch',
            padding: '4px 4px',
          }}
        >
          {render.kind === 'ready' ? (
            <img
              src={render.url}
              alt={`Your Stumble ${variant} card`}
              data-testid="recap-image"
              style={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                objectFit: 'contain',
                borderRadius: 12,
                boxShadow: '0 8px 0 var(--shadow-ink, rgba(0,0,0,0.18))',
              }}
            />
          ) : (
            <div className="body muted" style={{ textAlign: 'center', margin: 'auto' }}>
              <div className="spinner" aria-hidden style={{ marginInline: 'auto' }} />
              <p style={{ marginTop: 12 }}>rendering…</p>
            </div>
          )}
        </div>

        {/* G1.2: the "who else was out" leaderboard is already baked into the
            recap canvas image above (rendered server-side per variant). A
            second DOM-side leaderboard card duplicated the same data 9× in
            the DOM tree and pushed the canvas off-screen on small viewports.
            Deleted. The `leaderboard` array is still passed into metadata so
            the canvas continues to render it. */}

        {/* Action grid — download / share / home / copy link */}
        <div className="stack-sm" style={{ gap: 8 }}>
          <div className="row" style={{ gap: 10 }}>
            <button
              type="button"
              className="btn secondary full"
              onClick={handleDownload}
              data-testid="recap-download"
              aria-label="Download recap to camera roll"
              disabled={render.kind !== 'ready'}
            >
              <Icon name="check" size={18} /> Download
            </button>
            <button
              type="button"
              className="btn pink full"
              onClick={handleShare}
              data-testid="recap-share"
              aria-label="Share your recap"
              disabled={render.kind !== 'ready'}
            >
              <Icon name="share" size={18} color="var(--paper)" /> Share
            </button>
          </div>
          <div className="row" style={{ gap: 10 }}>
            <button
              type="button"
              className="btn secondary full"
              onClick={() => navigate({ to: '/' })}
              data-testid="recap-home"
            >
              home
            </button>
            <button
              type="button"
              className="btn secondary full"
              onClick={handleCopyLink}
              data-testid="recap-copy-link"
            >
              copy link
            </button>
          </div>
        </div>

        {shareMessage && <output className="toast">{shareMessage}</output>}
      </div>
    </div>
  )
}

/**
 * Pick a selfie URL for a given venue from the night's media assets. We
 * prefer the most recent matching media, falling back to any selfie in the
 * night if none match the venue.
 *
 * Source priority: publicUrl (R2 in prod) → storageKey proxy (legacy local)
 * → dataUrl (dev / test fallback). The scrapbook polaroid renderer accepts
 * any of these directly via <img src=…>.
 */
/**
 * F3: resolve a single media asset to a URL (R2 publicUrl > local proxy >
 * inline dataUrl). Returns empty string when nothing matches — caller filters.
 */
function pickMediaUrl(media: NightDetailsDto['media'][number]): string {
  if (media.publicUrl) return media.publicUrl
  if (media.storageKey) return `/api/media/${media.storageKey}`
  if (media.dataUrl) return media.dataUrl
  return ''
}

/**
 * F3: look up the venue name for a given media's venueId. Falls back to the
 * first session venue (most selfies happen there) or '—' so the polaroid
 * caption always renders something.
 */
function venueNameForMedia(details: NightDetailsDto, venueId: string | null): string {
  if (venueId) {
    const sv = details.sessionVenues.find((v) => v.venueId === venueId)
    if (sv) return sv.venue.name
  }
  return details.sessionVenues[0]?.venue.name ?? '—'
}

function pickSelfieForVenue(details: NightDetailsDto, venueId: string): string | null {
  if (!details.media || details.media.length === 0) return null
  const hasSource = (m: NightDetailsDto['media'][number]) =>
    Boolean(m.publicUrl || m.storageKey || m.dataUrl)
  const sorted = [...details.media].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
  const match = sorted.find((m) => m.venueId === venueId && hasSource(m))
  const fallback = sorted.find(hasSource)
  const chosen = match ?? fallback
  if (!chosen) return null
  if (chosen.publicUrl) return chosen.publicUrl
  if (chosen.storageKey) return `/api/media/${chosen.storageKey}`
  if (chosen.dataUrl) return chosen.dataUrl
  return null
}

function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}
